import React, { useEffect } from 'react';
import { RequestProvider, useFrame } from '~/shared/utils';
import type { NextPage } from 'next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import NextNProgress from 'nextjs-progressbar';
import { ReactElement, ReactNode } from 'react';
import { CommerceAPIProvider } from '~/features/commerce-api/contexts';
import { RaptorHeadScript } from '~/shared/scripts/RaptorHead';
import { useMarketState } from '~/shared/utils/market';
import { DynamicPageProps } from '~/templates/pages';
import { orangeMedium } from '~/theme/colors';
import { ThemeProvider } from '~/theme/context/ThemeProvider';
import { CacheProvider } from '@emotion/react';
import emotionCache from '~/services/emotion/cache';
import { SessionInitProvider } from '~/features/commerce-api/providers/SessionInitProvider';
import { GoogleTagManager } from '@next/third-parties/google';
import { Global, css } from '@emotion/react';
import { DinCondensedFont, EchoFont, RobotoFont } from '~/layout/fonts';

type NextPageWithLayout = NextPage<DynamicPageProps> & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps<DynamicPageProps> & {
    Component: NextPageWithLayout;
};

const isStaticErrorPage = (pathname: string): boolean =>
    pathname === '/404' || pathname === '/500' || pathname === '/_error';

// Pages that must not load GTM, e.g. the magiclink callback has a login token in its URL.
const PATHS_EXCLUDED_FROM_TRACKING = ['/magiclink/callback'];

//We want to expose the next/font families as CSS variables on :root so
// var(--typography-*) references resolve app-wide.
const FontVariables = (): JSX.Element => (
    <Global
        styles={css`
            :root {
                --typography-roboto: ${RobotoFont.style.fontFamily};
                --typography-din-condensed: ${DinCondensedFont.style.fontFamily};
                --typography-echo: ${EchoFont.style.fontFamily};
            }
        `}
    />
);

export const App = (props: AppPropsWithLayout): JSX.Element | null => {
    const { Component, pageProps, router } = props;
    const getLayout = Component.getLayout ?? ((page) => page);

    if (isStaticErrorPage(router.pathname)) {
        return (
            <>
                <FontVariables />
                {getLayout(<Component {...pageProps} />)}
            </>
        );
    }

    return (
        <>
            <FontVariables />
            <ConfiguredApp {...props} />
        </>
    );
};

const ConfiguredApp = ({
    Component,
    pageProps,
    router,
}: AppPropsWithLayout): JSX.Element | null => {
    const { setMarket } = useMarketState();
    const getLayout = Component.getLayout ?? ((page) => page);
    const { data: frame } = useFrame();
    const { page } = pageProps;

    const googleTagManagerId = PATHS_EXCLUDED_FROM_TRACKING.includes(router.pathname)
        ? undefined
        : frame.settings?.googleTagManagerId;
    const culture = frame.culture;

    useEffect(() => {
        setMarket({
            culture: culture ?? 'da-dk',
            market: page?.market ?? 'dk',
        });
    }, [frame.culture, page?.market]);

    // Send the tab order back to the navigation on page navigation, but skip if shallow.
    useEffect(() => {
        const resetTabOrder = (_url: string, { shallow }: { shallow: boolean }) => {
            if (shallow) return;

            document.getElementById('mainNavigation')?.focus();
        };

        router.events.on('routeChangeComplete', resetTabOrder);

        return () => router.events.off('routeChangeComplete', resetTabOrder);
    }, [router]);

    // TODO: This shouldn't be a requirement here but on the slug.
    if (!culture) return <></>;

    return (
        <>
            {/* Scripts */}
            {googleTagManagerId && (
                <GoogleTagManager
                    gtmScriptUrl="https://tracking.imerco.dk/gtm.js"
                    gtmId={googleTagManagerId}
                />
            )}
            <RaptorHeadScript customerId={frame.settings?.raptorCustomerId} />

            {/* GTM Styling */}
            {!!googleTagManagerId && (
                <noscript>
                    <iframe
                        src={`https://tracking.imerco.dk/ns.html?id=${googleTagManagerId}`}
                        height="0"
                        width="0"
                        style={{ display: 'none', visibility: 'hidden' }}
                    ></iframe>
                </noscript>
            )}

            {/* Progress bar */}
            <NextNProgress
                color={orangeMedium}
                height={4}
                startPosition={0.6}
                showOnShallow={false}
                options={{
                    showSpinner: false,
                    easing: 'ease',
                    speed: 500,
                    minimum: 0.3,
                    trickle: false,
                    trickleSpeed: 50,
                    parent: '#progress-loader',
                }}
            />

            {/* App */}
            <CommerceAPIProvider>
                <RequestProvider data={page?.data}>
                    <CacheProvider value={emotionCache}>
                        <ThemeProvider>
                            <SessionInitProvider>
                                {getLayout(<Component {...pageProps} />)}
                            </SessionInitProvider>
                        </ThemeProvider>
                    </CacheProvider>
                </RequestProvider>
            </CommerceAPIProvider>
        </>
    );
};

export function reportWebVitals(metric: NextWebVitalsMetric): void {
    if (process.env.NODE_ENV !== 'production') {
        console.debug('Webvitals', metric.name, Math.round(metric.value * 100) / 100, 'ms');
    }
}

export default App;
